/**
 * AI 응답 마크다운 렌더러
 * - [숫자] → ReferenceTag (문단) / ReferenceButton (테이블 셀)
 * - [RDB], [A] → KbCitationTag (문단). KB 자료가 있을 때만
 * - [[View Papers]] → PaperTitlesButton (refs ≥ 5)
 * - h2 제목이 reference와 일치 시 외부 링크 아이콘
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import PaperTitlesButton from "./paper-titles-button";
import ReferenceTag from "./reference-tag";
import KbCitationTag from "./kb-citation-tag";
import ReferenceButton from "./reference-button";
import { Reference } from "./message-list";
import { isFileTitle } from "@/components/ui/file-type-indicator";
import React from "react";
import {
  hasKnowledgeContent,
  type KbCitationTab,
  type KbContentData,
} from "@/utils/knowledge-content";
import { MathBlockSkeleton, mathCodeComponents } from "./markdown-math";
import TableScrollBox from "./table-scroll-box";

interface ResponseRendererProps {
  content: string;
  references: Reference[];
  /** 본문 [A]/[RDB] 태그가 열 Citation 데이터. 없으면 태그를 만들지 않는다 */
  knowledgeContent?: KbContentData | null;
  /** 메시지 단위 고유 키. KB 태그 강조가 다른 답변과 섞이지 않게 한다 */
  messageKey?: string;
}

/** walk/processCitationPattern에서 children 재귀 시 건너뛸 void 요소 (br, img 등) */
const VOID_ELEMENTS = [
  "br", "img", "hr", "input", "area", "base", "col", "embed",
  "link", "meta", "param", "source", "track", "wbr",
];

const isVoidElement = (type: unknown): boolean =>
  typeof type === "string" && VOID_ELEMENTS.includes(type);

const BLOCK_ELEMENTS = [ "p", "ul", "ol", "div", "table", "pre"];

const hasBlockChild = (children: React.ReactNode): boolean =>
  React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && typeof child.type === "string" && BLOCK_ELEMENTS.includes(child.type)
  );

/**
 * 테이블 셀용: [숫자] 제거하고 해당 refs만 수집
 * - walk: 트리 순회하며 refNumbers 수집 + [숫자] 텍스트 제거
 * - mergedRefs: 수집된 번호에 해당하는 references (셀당 ReferenceButton 1개용)
 */
function processAndCollectRefs(
  children: React.ReactNode,
  references: Reference[]
): { processed: React.ReactNode; mergedRefs: Reference[] } {
  const refNumbers = new Set<number>();

  function walk(node: React.ReactNode): React.ReactNode {
    const arr = React.Children.toArray(node);
    return arr.map((child) => {
      if (React.isValidElement(child)) {
        if (isVoidElement(child.type)) return child; // void는 children 없음
        if (child.props.children) {
          return React.cloneElement(child, {
            ...child.props,
            children: walk(child.props.children),
          } as any);
        }
        return child;
      }
      if (typeof child === "string") {
        const matches = child.matchAll(/\[(\d+)\]/g);
        for (const m of matches) {
          refNumbers.add(parseInt(m[1], 10));
        }
        // [숫자]와 바로 뒤 마침표 제거 (아이콘 뒤 . 방지)
        return child.replace(/\[\d+\]\.?/g, "").replace(/\s{2,}/g, " ").trim() || null;
      }
      return child;
    });
  }

  const processed = walk(children);
  const mergedRefs = (references ?? []).filter((r) =>
    refNumbers.has(r.reference_number)
  );
  return { processed, mergedRefs };
}

/** 본문 인라인 인용 토큰. [1] 숫자, [RDB], [A]~[Z] */
const CITATION_RE = /\[(\d+|RDB|[A-Z])\]/g;

interface KbCitationCtx {
  knowledgeContent?: KbContentData | null;
  /** 메시지 단위 고유 키. 다른 답변의 태그와 강조가 섞이지 않게 한다 */
  instanceKey?: string;
}

type CitationGroup =
  | { kind: "ref"; numbers: Set<number>; start: number; end: number }
  | { kind: "kb"; labels: string[]; tab: KbCitationTab; start: number; end: number };

/**
 * 문단(p)·목록(li)용: 인라인 인용 토큰을 태그 컴포넌트로 치환.
 *  - [1][2]  : 붙어 오면 한 ReferenceTag 로 묶는다. 쓸 출처가 없으면 글자로 남긴다.
 *  - [A][B]  : 같은 탭끼리 묶어 KbCitationTag 로. kb 컨텍스트가 없으면 글자로 남긴다.
 *  - 숫자와 KB 는 붙어 있어도 서로 묶지 않는다(여는 화면이 다르다).
 * React 엘리먼트는 children 만 재귀 처리한다.
 */
export function processCitationPattern(
  children: React.ReactNode,
  references: Reference[],
  kb?: KbCitationCtx
): React.ReactNode {
  return React.Children.toArray(children).map((child, index) => {
    if (React.isValidElement(child)) {
      if (isVoidElement(child.type)) return child;
      const element = child as React.ReactElement<{ children?: React.ReactNode }>;
      if (!element.props.children) return child;
      return React.cloneElement(element, {
        children: processCitationPattern(element.props.children, references, kb),
      });
    }
    if (typeof child !== "string") return child;

    const text = child;
    const matches = [...text.matchAll(CITATION_RE)];
    if (matches.length === 0) return text;

    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let group: CitationGroup | null = null;
    /** 숫자 토큰이 하나라도 있었으면 꼬리의 마침표를 지운다(아이콘 뒤 . 방지) */
    let hadRefToken = false;

    const flush = () => {
      if (!group) return;
      if (group.start > lastIndex) {
        parts.push(text.substring(lastIndex, group.start));
      }

      if (group.kind === "ref") {
        /* 열어서 보여줄 게 있는 번호만 태그로 만든다(주소 또는 파일) */
        const numbers = group.numbers;
        const groupRefs = (references ?? []).filter(
          (r) => numbers.has(r.reference_number) && (r.url?.trim() || isFileTitle(r.title))
        );
        parts.push(
          groupRefs.length > 0 ? (
            <ReferenceTag key={`ref-${index}-${group.start}`} references={groupRefs} />
          ) : (
            text.substring(group.start, group.end)
          )
        );
      } else {
        parts.push(
          <KbCitationTag
            key={`kb-${index}-${group.start}`}
            labels={group.labels}
            tab={group.tab}
            knowledgeContent={kb?.knowledgeContent}
            instanceKey={kb?.instanceKey}
          />
        );
      }
      lastIndex = group.end;
      group = null;
    };

    for (const m of matches) {
      const token = m[1];
      const start = m.index ?? 0;
      const end = start + m[0].length;
      const isRef = /^\d+$/.test(token);
      /* kb 컨텍스트가 없으면 KB 토큰은 손대지 않는다(열 자료가 없다) */
      if (!isRef && !kb) continue;

      /* group 은 flush 안에서도 바뀌어 TS 가 null 로 좁히지 않게 타입을 둔다 */
      const current = group as CitationGroup | null;
      const between = current ? text.substring(current.end, start) : "";
      const spaced = /^[,\s]+$/.test(between);

      if (isRef) {
        hadRefToken = true;
        if (current?.kind === "ref" && (start <= current.end + 1 || spaced)) {
          current.numbers.add(Number(token));
          current.end = end;
          continue;
        }
        flush();
        group = { kind: "ref", numbers: new Set([Number(token)]), start, end };
        continue;
      }

      const tab: KbCitationTab = token === "RDB" ? "rdb" : "unstructured";
      if (current?.kind === "kb" && current.tab === tab && (start === current.end || spaced)) {
        current.labels.push(token);
        current.end = end;
        continue;
      }
      flush();
      group = { kind: "kb", labels: [token], tab, start, end };
    }
    flush();

    if (lastIndex < text.length) {
      const tail = text.substring(lastIndex);
      parts.push(hadRefToken ? tail.replace(/^\.*/, "") : tail);
    }

    return <React.Fragment key={`cite-${index}`}>{parts}</React.Fragment>;
  });
}

/**
 * 스트리밍 중 미완성 $$ 블록을 스켈레톤 마커로 대체
 * - "$$" 기준 split → 짝수 파트 수(홀수 개의 $$) = 마지막 블록이 닫히지 않음
 */
const MATH_LOADING_MARKER = "[Rendering equation...]";

function preprocessForStreaming(raw: string): string {
  const parts = raw.split("$$");
  if (parts.length % 2 === 0) {
    // 마지막 미완성 $$ 블록 → 마커로 대체
    return parts.slice(0, -1).join("$$") + "\n\n" + MATH_LOADING_MARKER + "\n\n";
  }
  return raw;
}

function ResponseRenderer({ content, references, knowledgeContent, messageKey }: ResponseRendererProps) {
  /* KB 콘텐츠가 없으면 [A]/[RDB] 는 평범한 글자로 남는다(열 자료가 없으므로) */
  const kbCtx: KbCitationCtx | undefined = hasKnowledgeContent(knowledgeContent)
    ? { knowledgeContent, instanceKey: messageKey }
    : undefined;
  // 스트리밍 중 미완성 $$ 블록을 스켈레톤 마커로 전처리
  const processedContent = preprocessForStreaming(content);

  const components = {
    ...mathCodeComponents,
    /** "숫자. 제목" 형태에서 제목이 references에 있으면 외부 링크 아이콘 표시 */
    h2: ({ children }: { children?: React.ReactNode }) => {
      const text = String(children);
      const match = text.match(/^\d+\.\s+(.+)$/);

      if (match) {
        const title = match[1];
        const ref = references?.find((r) => r.title === title);

        if (ref) {
          return (
            <div className="flex items-center gap-2">
              <h2 className="pt-2 pb-1 m-0">{children}</h2>
              <div className="min-w-[40px] min-h-[40px] flex justify-center items-center">
                <a href={ref.url} target="_blank" rel="noopener noreferrer">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H12V5H5V19H19V12H21V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5ZM9.7 15.7L8.3 14.3L17.6 5H14V3H21V10H19V6.4L9.7 15.7Z"
                      fill="#1DB3A4"
                    />
                  </svg>
                </a>
              </div>
            </div>
          );
        }
      }

      return <h2>{children}</h2>;
    },
    /** [[View Papers]] → PaperTitlesButton(refs≥5) / 빈요소(refs<5), 그 외 [숫자] → ReferenceTag */
    p: ({ children }: { children?: React.ReactNode }) => {
      const childArray = React.Children.toArray(children);

      // 스트리밍 중 미완성 $$ 블록 → 스켈레톤
      const hasMathLoading = childArray.some(
        (c) => typeof c === "string" && c.includes(MATH_LOADING_MARKER)
      );
      if (hasMathLoading) {
        return <MathBlockSkeleton />;
      }

      if (childArray.length === 1 && typeof childArray[0] === "string") {
        const text = childArray[0];
        const match = text.match(/^\[\[(.+)\]\]$/);
    
        const isViewPapers = match && (match[1] === "View Papers" || match[1] === "논문 보기");
        if (isViewPapers && references?.length >= 5) {
          return <PaperTitlesButton references={references ?? []} />;
        } else if (isViewPapers && (references?.length ?? 0) < 5) {
          return <></>;
        }
      }

      const processedChildren = processCitationPattern(children, references, kbCtx);

      return <p>{processedChildren}</p>;
    },
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
      <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
      <ul style={{ lineHeight: "27px", letterSpacing: "0.5px" }}>{children}</ul>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
      <ol className="space-y-4">{children}</ol>
    ),
    li: ({ children }: { children?: React.ReactNode }) => {
      if (hasBlockChild(children)) {
        return <li>{children}</li>;
      }

      const processedChildren = processCitationPattern(children, references, kbCtx);
      return (
        <li>
          <p>{processedChildren}</p>
        </li>
      );
    },
    hr: () => {
      return <hr className="mt-[32px] mb-[14px] border-grey-90" />;
    },
    /* 컬럼이 많은 표는 본문 폭에 안 들어가고 표는 폭을 줄일 수도 없다.
       본문 오른쪽으로 자라다 채팅 페인 끝에서 멈추고 그 안에서 가로 스크롤된다. 폭은 .answer-table 이 정한다 */
    table: ({ children }: { children?: React.ReactNode }) => {
      return (
        <TableScrollBox>
          <table className="answer-table">{children}</table>
        </TableScrollBox>
      );
    },
    /** tr: 각 셀에 columnIndex 주입 (두 번째 열에서 Reference 미생성용) */
    tr: ({ children }: { children?: React.ReactNode }) => {
      return (
        <tr>
          {React.Children.map(children, (child, columnIndex) =>
            React.isValidElement(child)
              ? React.cloneElement(child, { columnIndex } as any)
              : child
          )}
        </tr>
      );
    },
    /** • 기준 줄바꿈 + [숫자] 제거 후 셀당 ReferenceButton 1개 (두 번째 열 제외) */
    td: ({
      children,
      columnIndex,
    }: {
      children?: React.ReactNode;
      columnIndex?: number;
    }) => {
      const withLineBreaks = React.Children.map(children, (child) => {
        if (typeof child === "string") {
          const parts = child.split(/(?=• )/g).filter((part) => part.trim());
          if (parts.length > 1) {
            return parts.map((part, index) => (
              <React.Fragment key={index}>
                {index > 0 && <br />}
                {part}
              </React.Fragment>
            ));
          }
        }
        return child;
      });

      if (columnIndex === 1) {
        return <td className="align-middle">{withLineBreaks}</td>;
      }

      const { processed, mergedRefs } = processAndCollectRefs(
        withLineBreaks,
        references
      );
      return (
        <td className="align-middle">
          {processed}
          {mergedRefs.length > 0 && (
            <div className="inline-flex items-center min-h-[40px] max-h-[40px] h-[40px] shrink-0 overflow-hidden rounded-[8px]">
              <ReferenceButton references={mergedRefs} hideLabel />
            </div>
          )}
        </td>
      );
    },
    /** td와 동일: • 줄바꿈 + [숫자] 제거 후 ReferenceButton 1개 (두 번째 열 제외) */
    th: ({
      children,
      columnIndex,
    }: {
      children?: React.ReactNode;
      columnIndex?: number;
    }) => {
      const withLineBreaks = React.Children.map(children, (child) => {
        if (typeof child === "string") {
          const parts = child.split(/(?=• )/g).filter((part) => part.trim());
          if (parts.length > 1) {
            return parts.map((part, index) => (
              <React.Fragment key={index}>
                {index > 0 && <br />}
                {part}
              </React.Fragment>
            ));
          }
        }
        return child;
      });

      if (columnIndex === 1) {
        return <th className="align-middle">{withLineBreaks}</th>;
      }

      const { processed, mergedRefs } = processAndCollectRefs(
        withLineBreaks,
        references
      );
      return (
        <th className="align-middle">
          {processed}
          {mergedRefs.length > 0 && (
            <div className="inline-flex items-center min-h-[40px] max-h-[40px] h-[40px] shrink-0 overflow-hidden rounded-[8px]">
              <ReferenceButton references={mergedRefs} hideLabel />
            </div>
          )}
        </th>
      );
    },
  };

  return (
    <ReactMarkdown
      components={components}
      remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkMath]}
      rehypePlugins={[rehypeKatex]}
    >
      {processedContent}
    </ReactMarkdown>
  );
}

export default ResponseRenderer;
